/**
 * The screen where a connecting advisor confirms how their CRM's fields map onto Waterlily's.
 *
 * One screen serves every CRM: the provider comes from the route, and the only thing that changes
 * is the name shown and whether the CRM's discovery reflects permissions. Only the advisor who owns
 * the connection sees or sets this, so the page speaks in terms of "your CRM".
 *
 * The fields are shown in sections by what they need from the advisor: a choice to make, a match to
 * check, nothing, or nothing possible. Most of the work is already done by the matcher, so the page
 * leads with what is left and keeps the confirm button in view at the foot.
 *
 * A request that failed is shown as a failure, never as an absence. A field list that never arrived
 * is not described as a CRM with no fields, a status that could not be fetched is not described as
 * contacts never synced, and a sync or setting the server refused shows the server's reason. A field
 * list already on screen stays there when a refresh fails.
 *
 * All state and submission live in the hooks; this file only lays the parts out. The parts are in
 * `features/crm-field-map`.
 */
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton, Spinner } from '@design-system';
import type { Prospecting } from '@client/api/types';
import { BookReadConsent } from '@client/features/crm-field-map/components/book-read-consent';
import { BookSyncStatus } from '@client/features/crm-field-map/components/book-sync-status';
import { DuplicatePeople } from '@client/features/crm-field-map/components/duplicate-people';
import { FieldMapActionBar } from '@client/features/crm-field-map/components/field-map-action-bar';
import { FieldMapAlert } from '@client/features/crm-field-map/components/field-map-alert';
import { FieldMapGroup } from '@client/features/crm-field-map/components/field-map-group';
import { FieldMapRow } from '@client/features/crm-field-map/components/field-map-row';
import { FieldMapSummary } from '@client/features/crm-field-map/components/field-map-summary';
import { fieldMapCopy } from '@client/features/crm-field-map/lib/field-map-copy';
import { UNMAPPED } from '@client/features/crm-field-map/lib/field-map-groups';
import { useCrmBookSync } from '@client/hooks/useCrmBookSync';
import { useCrmConsent } from '@client/hooks/useCrmConsent';
import { useCrmDuplicates } from '@client/hooks/useCrmDuplicates';
import { useCrmFieldMap } from '@client/hooks/useCrmFieldMap';
import { useSyncFinishedToast } from '@client/hooks/useSyncFinishedToast';
import { selectSessionAdvisor } from '@client/model/selectors';
import { crmName as crmDisplayName } from '@/util/crmNames';

export function AdvisorIntegrationsCrmFieldMap() {
  const navigate = useNavigate();
  const { provider = 'salesforce' } = useParams<{ provider: Prospecting.Provider }>();
  const crmName = crmDisplayName(provider);

  const fieldMap = useCrmFieldMap(provider as Prospecting.Provider);
  const duplicates = useCrmDuplicates();

  const advisor = useSelector(selectSessionAdvisor);
  const consent = useCrmConsent({
    advisorId: advisor?.advisorId ?? '',
    provider: provider as string,
  });
  const sync = useCrmBookSync(provider as Prospecting.Provider, {
    bookReadConsented: consent.hasBookReadConsent,
  });
  useSyncFinishedToast(sync, crmName);

  // Only a known "no" locks the sync; a lookup still loading or failed is not a "no".
  const consentMissing = !consent.isLoading && !consent.isError && !consent.hasBookReadConsent;
  const neverLoaded = !fieldMap.hasLoaded && !fieldMap.isLoading;

  return (
    <main className="px-4 pt-3 sm:px-6 lg:px-8">
      <div className="my-7 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-3 text-gray-600"
          >
            <ArrowLeft />
            {fieldMapCopy.back}
          </Button>
          <h1 className="text-primary-dark text-4xl font-semibold">{fieldMapCopy.pageTitle(crmName)}</h1>
        </div>
        <Button
          variant="outline"
          onClick={() => fieldMap.refetch()}
          disabled={fieldMap.isFetching}
        >
          {fieldMap.isFetching ? <Spinner aria-hidden="true" /> : <RefreshCw className="h-4 w-4" />}
          {fieldMap.isFetching && fieldMap.hasLoaded ? fieldMapCopy.refreshingFields : fieldMapCopy.refreshFields}
        </Button>
      </div>
      <div className="bg-primary-100 my-6 h-px w-full" />

      <BookReadConsent
        crmName={crmName}
        consent={consent}
      />

      <BookSyncStatus
        crmName={crmName}
        sync={sync}
        consentMissing={consentMissing}
      />

      <DuplicatePeople duplicates={duplicates} />

      <Card>
        <CardHeader>
          <CardTitle>{fieldMapCopy.fieldsCardTitle}</CardTitle>
          <CardDescription>
            {fieldMapCopy.fieldsIntro(crmName)}{' '}
            {!fieldMap.hasLoaded
              ? null
              : fieldMap.capabilities?.discoveryReflectsPermissions
                ? fieldMapCopy.fieldsReadable(fieldMap.readableFieldCount, crmName)
                : fieldMapCopy.fieldsFound(fieldMap.readableFieldCount, crmName)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!!fieldMap.error && (
            <FieldMapAlert
              title={neverLoaded ? fieldMapCopy.alerts.fieldsFirstTitle : fieldMapCopy.alerts.fieldsTitle}
              error={fieldMap.error}
              onRetry={() => fieldMap.refetch()}
              className="mb-4"
            />
          )}
          {!!fieldMap.saveError && (
            <FieldMapAlert
              title={fieldMapCopy.alerts.saveTitle}
              error={fieldMap.saveError}
              className="mb-4"
            />
          )}

          {fieldMap.isLoading ? (
            <div
              role="status"
              className="space-y-3"
            >
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <Spinner aria-hidden="true" />
                {fieldMapCopy.loadingFields(crmName)}
              </p>
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="h-14 w-full"
                />
              ))}
            </div>
          ) : neverLoaded ? null : (
            <>
              <FieldMapSummary
                summary={fieldMap.summary}
                pendingCount={fieldMap.pendingCount}
              />
              <div className="space-y-4">
                {fieldMap.groups.map(group => (
                  <FieldMapGroup
                    key={group.key}
                    group={group.key}
                    count={group.rows.length}
                    crmName={crmName}
                  >
                    {group.rows.map(row => (
                      <FieldMapRow
                        key={row.canonicalField}
                        row={row}
                        fields={fieldMap.fields}
                        crmName={crmName}
                        disabled={fieldMap.isSaving}
                        onSelect={fieldMap.select}
                        onMeaning={fieldMap.setSemantics}
                      />
                    ))}
                  </FieldMapGroup>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {fieldMap.hasLoaded && (
        <FieldMapActionBar
          pendingCount={fieldMap.pendingCount}
          removalCount={fieldMap.removalCount}
          missingScaleCount={fieldMap.missingScaleCount}
          hasMappedRows={fieldMap.rows.some(row => row.selected !== UNMAPPED)}
          hasConsent={consent.hasBookReadConsent}
          hasUnsavedEdits={fieldMap.hasUnsavedEdits}
          isSaving={fieldMap.isSaving}
          onSave={fieldMap.save}
          onDiscard={fieldMap.reset}
        />
      )}
      {!fieldMap.hasLoaded && <div className="pb-12" />}
    </main>
  );
}
